import { ChangeEvent, FC, useState, useCallback, useEffect, useMemo } from 'react';
import { debounce } from 'throttle-debounce';
import { useTranslation } from 'react-i18next';
// Models
import { ICurrentAccount } from 'app/models/Account';
import { IPrompt } from 'app/store/AIChatPrompts/AIChatPrompts.models';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Actions
import { AIChatPromptsActions } from 'app/store/AIChatPrompts/AIChatPrompts.slice';
// Selectors
import { selectAIChatPlatformPromptsHidden, selectCurrentAccount } from 'app/store/Accounts/Accounts.selectors';
import { selectFilter, selectFilteredPrompts } from 'app/store/AIChatPrompts/AIChatPrompts.selectors';
// Mui
import { Theme, Box, Tabs, Tab } from '@mui/material';
// Components
import Dialog from 'app/components/Dialog';
import DataLoading from 'app/components/DataLoading';
import AIPromptCard from 'app/components/AIPromptCard';
import { Input } from 'app/components/Mui';
import { getPrompts } from 'app/store/AIChatPrompts/AIChatPrompts.async';
import { selectInsuranceCase } from 'app/store/Cases/Cases.selectors';
import IInsuranceCase from 'app/models/Case';

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (aiPrompt:IPrompt) => void;
}

const AIPromptSelectDialog:FC<Props> = ({
  // Props
  open, onClose, onConfirm
}) => {
  const { t } = useTranslation();

  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const currentAccount = useAppSelector(selectCurrentAccount) as ICurrentAccount;
  const aIChatPlatformPromptsHidden = useAppSelector(selectAIChatPlatformPromptsHidden);
  const insuranceCase = useAppSelector(selectInsuranceCase) as IInsuranceCase;
  const prompts = useAppSelector(selectFilteredPrompts);
  const filter = useAppSelector(selectFilter);

  const data = useMemo(() => {
    if ( !prompts ) return null
    return prompts.reduce((acc:{ platformPrompts:IPrompt[], regularPrompts:IPrompt[] }, cur:IPrompt) => {
      if ( cur.platform ){
        acc.platformPrompts.push(cur);
      } else {
        acc.regularPrompts.push(cur);
      }
      return acc;
    }, { platformPrompts: [], regularPrompts:[] });
    // eslint-disable-next-line
  }, [prompts]);

  // eslint-disable-next-line
  const debounceFilter = useCallback(debounce(500, (field:keyof typeof filter, value:any) => {
    dispatch(AIChatPromptsActions.setFilter({ field, value }))
  }), []);

  const [ stateFilter, setStateFilter ] = useState(filter);
  const [ tab, setTab ] = useState(0);

  useEffect(() => {
    if ( prompts?.length ) return;
    const teamId:number | undefined = insuranceCase.team?.id;

    dispatch(getPrompts({ teamId, owners: 'self,platform' }));

    return () => {
      dispatch(AIChatPromptsActions.setInitialField('filter'));
    }
    // eslint-disable-next-line
  }, [])

  const handleChange = (event:ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setStateFilter((prevState:any) => ({
      ...prevState,
      [name]: value
    }));
    debounceFilter(name as keyof typeof filter, value);
  }

  const handleClick = (aiPrompt:IPrompt) => () => {
    onConfirm(aiPrompt);
    onClose();
  }

  const handleChangeTab = (_:any, nextTab:number) => {
    setTab(nextTab);
  }
  
  const handleClearSearch = () => {
    setStateFilter((prevState) => ({
      ...prevState,
      search: ''
    }));
    dispatch(AIChatPromptsActions.setFilter({ field: 'search', value: '' }));
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title={t('pages.aiPromptHub.selectDialogTitle')}
    >
      <Box sx={{ display: 'flex', mb: 4 }}>
        <Input
          label={t('labels.search')} id="search" name="search"
          placeholder={t('form.placeholder.nameOrLabel')}
          value={stateFilter.search}
          onChange={handleChange}
          onClear={handleClearSearch}
          size="small"
          margin="none"
        />
      </Box>
      <Tabs
        sx={{
          borderBottom: (theme:Theme) => `1px solid ${theme.palette.divider}`,
          mb: 4
        }}
        value={tab}
        onChange={handleChangeTab}
        centered
      >
        <Tab sx={{ textTransform: 'capitalize' }} label={currentAccount.company} />
        {aIChatPlatformPromptsHidden ? null : <Tab sx={{ textTransform: 'capitalize' }} label="Case Chronology" />}
      </Tabs>
      <Box sx={{ display: tab === 0 ? 'block' : 'none', height: 600 }}>
        <DataLoading
          data={data?.regularPrompts}
          text={t("pages.aiPromptHub.noData")}
        >{(data) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {data.map((aiPrompt) => (
              <AIPromptCard
                key={`ai prompt item ${aiPrompt.id}`}
                aiPrompt={aiPrompt}
                onClick={handleClick(aiPrompt)}
              />
            ))}
          </Box>
        )}</DataLoading>
      </Box>
      {aIChatPlatformPromptsHidden ? null : (
        <Box sx={{ display: tab === 1 ? 'block' : 'none', height: 600 }}>
          <DataLoading
            data={data?.platformPrompts}
            text={t("pages.aiPromptHub.noData")}
          >{(data) => (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {data.map((aiPrompt) => (
                <AIPromptCard
                  key={`ai prompt item ${aiPrompt.id}`}
                  aiPrompt={aiPrompt}
                  onClick={handleClick(aiPrompt)}
                />
              ))}
            </Box>
          )}</DataLoading>
        </Box>
      )}
    </Dialog>
  )
}

export default AIPromptSelectDialog;
